/**
 * Order Wizard — "Walk Me Through Purchasing"
 *
 * Multi-step wizard that guides parents through ordering uniforms
 * for each child: auth → children → per-child (profile → sizes → tops → bottoms → outerwear → accessories → summary).
 */
(function () {
  'use strict';

  // ── Config ──────────────────────────────────────────
  const FIREBASE_BASE = 'https://us-central1-max-and-alice-shopify-ydm-app.cloudfunctions.net';
  const CATEGORIES = ['tops', 'bottoms', 'outerwear', 'accessories'];

  // Size ladder for "move all up one" — ordered small → large
  const NUMERIC_SIZES = ['3','4','5','6','7','8','10','12','14','16','18','20'];
  const LETTER_SIZES = ['YXS','YS','YM','YL','YXL','XS','S','M','L','XL','XXL'];
  // Max size thresholds: boys top out at 20, girls at 18
  const MAX_BOYS_NUMERIC = 20;
  const MAX_GIRLS_NUMERIC = 18;
  const WAIST_BUMP_INCHES = 1.5;

  // Sizing CSV URLs (will be overridden from page config if available)
  let sizingConfig = {
    requirementsUrl: '',
    sizingUrl: ''
  };

  // ── State ───────────────────────────────────────────
  let children = [];
  let selectedChildIds = new Set(); // children selected for shopping
  let shoppingChildren = []; // filtered subset of children to shop for
  let currentChildIndex = 0;
  let selections = {}; // { childId: [ { product, variantId, variant, qty, category } ] }
  let sizingData = [];
  let requirementsData = [];
  let schoolCollections = []; // cached
  let productCache = {};
  let cutPreference = 'both'; // 'girls' | 'unisex' | 'both'

  // Steps (in order per child)
  const CHILD_STEPS = ['profile', 'sizes', 'tops', 'bottoms', 'outerwear', 'accessories', 'summary'];
  let currentStep = 'auth'; // 'auth' | 'children' | one of CHILD_STEPS
  let stepHistory = [];

  // ── DOM refs ────────────────────────────────────────
  const $ = (id) => document.getElementById(id);
  const overlay = $('orderWizard');
  if (!overlay) return; // snippet not rendered

  const els = {
    overlay,
    title: $('owTitle'),
    backBtn: $('owBackBtn'),
    closeBtn: $('owCloseBtn'),
    progressBar: $('owProgressBar'),
    // Steps
    stepAuth: $('owStepAuth'),
    stepChildren: $('owStepChildren'),
    stepProfile: $('owStepProfile'),
    stepSizes: $('owStepSizes'),
    stepCategory: $('owStepCategory'),
    stepSummary: $('owStepSummary'),
    // Children
    childrenList: $('owChildrenList'),
    addChildBtn: $('owAddChildBtn'),
    addChildForm: $('owAddChildForm'),
    childFirstName: $('owChildFirstName'),
    childLastName: $('owChildLastName'),
    childSchool: $('owChildSchool'),
    childGrade: $('owChildGrade'),
    childGender: $('owChildGender'),
    saveChildBtn: $('owSaveChildBtn'),
    cancelChildBtn: $('owCancelChildBtn'),
    startShoppingBtn: $('owStartShoppingBtn'),
    // Profile
    profileSchool: $('owProfileSchool'),
    profileGrade: $('owProfileGrade'),
    profileGender: $('owProfileGender'),
    profileNextBtn: $('owProfileNextBtn'),
    // Sizes
    existingSizes: $('owExistingSizes'),
    manualSizes: $('owManualSizes'),
    currentTopSize: $('owCurrentTopSize'),
    currentBottomSize: $('owCurrentBottomSize'),
    currentOuterwearSize: $('owCurrentOuterwearSize'),
    bumpSizesBtn: $('owBumpSizesBtn'),
    toggleMeasurements: $('owToggleMeasurements'),
    measurementForm: $('owMeasurementForm'),
    measChest: $('owMeasChest'),
    measWaist: $('owMeasWaist'),
    measHips: $('owMeasHips'),
    measInseam: $('owMeasInseam'),
    measWaistKnee: $('owMeasWaistKnee'),
    measShoulder: $('owMeasShoulder'),
    resolveSizesBtn: $('owResolveSizesBtn'),
    sizeResults: $('owSizeResults'),
    inputTopSize: $('owInputTopSize'),
    inputBottomSize: $('owInputBottomSize'),
    inputOuterwearSize: $('owInputOuterwearSize'),
    sizesNextBtn: $('owSizesNextBtn'),
    sizesSubtext: $('owSizesSubtext'),
    // Category
    catHeading: $('owCatHeading'),
    catSubtext: $('owCatSubtext'),
    girlsCutPref: $('owGirlsCutPref'),
    productGrid: $('owProductGrid'),
    catNextBtn: $('owCatNextBtn'),
    // Summary
    summaryItems: $('owSummaryItems'),
    summaryEmpty: $('owSummaryEmpty'),
    addAllToCartBtn: $('owAddAllToCartBtn'),
    nextChildPrompt: $('owNextChildPrompt'),
    nextChildBtn: $('owNextChildBtn'),
    finishBtn: $('owFinishBtn'),
  };

  // ── Init ────────────────────────────────────────────
  function init() {
    // Load sizing config from page
    try {
      const configEl = document.getElementById('ow-sizing-config');
      if (configEl) {
        const parsed = JSON.parse(configEl.textContent);
        if (parsed.requirementsUrl) sizingConfig.requirementsUrl = parsed.requirementsUrl;
        if (parsed.sizingUrl) sizingConfig.sizingUrl = parsed.sizingUrl;
      }
    } catch (e) { /* ignore */ }

    // Also try the live theme config
    if (window.MA_SIZING_CONFIG) {
      sizingConfig.requirementsUrl = sizingConfig.requirementsUrl || window.MA_SIZING_CONFIG.requirementsUrl;
      sizingConfig.sizingUrl = sizingConfig.sizingUrl || window.MA_SIZING_CONFIG.sizingUrl;
    }

    // Style the "Move All Sizes Up One" button — blue with gold text
    if (els.bumpSizesBtn) {
      els.bumpSizesBtn.style.cssText = 'background:#153640; color:#D9A864; border:1px solid #D9A864; font-weight:700;';
    }

    bindEvents();
  }

  // ── Event binding ───────────────────────────────────
  function bindEvents() {
    // Global open triggers
    document.addEventListener('click', (e) => {
      if (e.target.closest('.js-open-order-wizard')) {
        e.preventDefault();
        open();
      }
    });

    els.closeBtn.addEventListener('click', close);
    els.backBtn.addEventListener('click', goBack);
    els.overlay.addEventListener('click', (e) => {
      if (e.target === els.overlay) close();
    });

    // Children step
    els.addChildBtn.addEventListener('click', () => toggleAddChildForm(true));
    els.cancelChildBtn.addEventListener('click', () => toggleAddChildForm(false));
    els.saveChildBtn.addEventListener('click', saveNewChild);
    els.startShoppingBtn.addEventListener('click', startShoppingForChildren);

    // Profile step
    els.profileNextBtn.addEventListener('click', saveProfileAndNext);

    // Sizes step
    els.bumpSizesBtn.addEventListener('click', bumpAllSizes);
    els.toggleMeasurements.addEventListener('click', toggleMeasurementEntry);
    els.resolveSizesBtn.addEventListener('click', resolveSizesFromMeasurements);
    els.sizesNextBtn.addEventListener('click', saveSizesAndNext);

    // Category step
    els.catNextBtn.addEventListener('click', nextCategory);
    els.girlsCutPref.addEventListener('click', (e) => {
      const btn = e.target.closest('.ow-cut-btn');
      if (!btn) return;
      cutPreference = btn.dataset.cut;
      els.girlsCutPref.querySelectorAll('.ow-cut-btn').forEach(b => b.classList.remove('ow-cut-btn--active'));
      btn.classList.add('ow-cut-btn--active');
      renderCategoryProducts();
    });

    // Summary step
    els.addAllToCartBtn.addEventListener('click', addAllToCart);
    els.nextChildBtn.addEventListener('click', goToNextChild);
    els.finishBtn.addEventListener('click', finishAndViewCart);

    // Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !els.overlay.classList.contains('tw-hidden')) close();
    });
  }

  // ── Open / Close ────────────────────────────────────
  function open() {
    stepHistory = [];
    currentChildIndex = 0;
    selections = {};
    cutPreference = 'both';
    selectedChildIds = new Set();
    shoppingChildren = [];

    // Check if logged in
    const customerId = getCustomerId();
    if (!customerId) {
      goToStep('auth');
    } else {
      loadChildrenAndShow();
    }

    els.overlay.classList.remove('tw-hidden');
    document.body.style.overflow = 'hidden';
  }

  function close() {
    els.overlay.classList.add('tw-hidden');
    document.body.style.overflow = '';
  }

  function getCustomerId() {
    const el = document.querySelector('.customer-id[data-customer-id]');
    if (el) return el.dataset.customerId;
    // Also check body data-id
    const bodyId = document.body.dataset.id;
    return bodyId && bodyId !== '' ? bodyId : null;
  }

  // ── Navigation ──────────────────────────────────────
  function goToStep(step) {
    if (currentStep && currentStep !== step) {
      stepHistory.push(currentStep);
    }
    currentStep = step;

    // Hide all steps
    [els.stepAuth, els.stepChildren, els.stepProfile, els.stepSizes, els.stepCategory, els.stepSummary]
      .forEach(el => el.classList.add('tw-hidden'));

    // Show back button (except on auth/children)
    els.backBtn.classList.toggle('tw-hidden', step === 'auth' || step === 'children');

    // Update progress
    updateProgress();

    // Show the right step
    switch (step) {
      case 'auth':
        els.stepAuth.classList.remove('tw-hidden');
        els.title.textContent = 'Order Wizard';
        break;
      case 'children':
        els.stepChildren.classList.remove('tw-hidden');
        els.title.textContent = 'Your Children';
        renderChildrenList();
        break;
      case 'profile':
        els.stepProfile.classList.remove('tw-hidden');
        els.title.textContent = 'Verify Profile';
        renderProfileStep();
        break;
      case 'sizes':
        els.stepSizes.classList.remove('tw-hidden');
        els.title.textContent = 'Verify Sizes';
        renderSizesStep();
        break;
      case 'tops':
      case 'bottoms':
      case 'outerwear':
      case 'accessories':
        els.stepCategory.classList.remove('tw-hidden');
        els.title.textContent = capitalize(step);
        renderCategoryStep(step);
        break;
      case 'summary':
        els.stepSummary.classList.remove('tw-hidden');
        els.title.textContent = 'Review & Add to Cart';
        renderSummaryStep();
        break;
    }
  }

  function goBack() {
    if (stepHistory.length === 0) return;
    const prev = stepHistory.pop();
    currentStep = null; // prevent double-push
    goToStep(prev);
    // Remove the duplicate that goToStep pushed
    if (stepHistory.length > 0 && stepHistory[stepHistory.length - 1] === prev) {
      stepHistory.pop();
    }
  }

  function updateProgress() {
    let pct = 0;
    if (currentStep === 'auth') pct = 0;
    else if (currentStep === 'children') pct = 5;
    else {
      const childStepIdx = CHILD_STEPS.indexOf(currentStep);
      if (childStepIdx >= 0) {
        const totalChildSteps = (shoppingChildren.length || children.length) * CHILD_STEPS.length;
        const completedSteps = currentChildIndex * CHILD_STEPS.length + childStepIdx;
        pct = 5 + (completedSteps / totalChildSteps) * 95;
      }
    }
    els.progressBar.style.width = Math.round(pct) + '%';
  }

  // ── Children Step ───────────────────────────────────
  async function loadChildrenAndShow() {
    goToStep('children');
    els.childrenList.innerHTML = '<div class="ow-loading">Loading children...</div>';

    const customerId = getCustomerId();
    try {
      const res = await fetch(`${FIREBASE_BASE}/getChildren?customerId=${customerId}`);
      const data = await res.json();
      children = Array.isArray(data) ? data : (data.children || []);
    } catch (e) {
      console.error('Failed to load children:', e);
      children = [];
    }

    // Also preload school collections
    loadSchoolCollections();

    renderChildrenList();
  }

  function renderChildrenList() {
    els.childrenList.innerHTML = '';

    if (children.length === 0) {
      els.childrenList.innerHTML = '<p class="ow-subtext">No children added yet. Add your first child below.</p>';
      els.startShoppingBtn.classList.add('tw-hidden');
      return;
    }

    els.startShoppingBtn.classList.remove('tw-hidden');

    // Select all children by default if none are selected yet
    if (selectedChildIds.size === 0) {
      children.forEach(child => {
        if (child.id) selectedChildIds.add(child.id);
      });
    }

    children.forEach((child, idx) => {
      const childId = child.id || `child-${idx}`;
      const isSelected = selectedChildIds.has(childId);

      const card = document.createElement('div');
      card.className = 'ow-child-card';
      card.style.cssText = `cursor:pointer; ${isSelected ? '' : 'opacity:0.5;'}`;
      card.innerHTML = `
        <input type="checkbox" class="ow-child-check" ${isSelected ? 'checked' : ''}
          style="width:20px; height:20px; accent-color:#153640; cursor:pointer; flex-shrink:0;">
        <img class="ow-child-card__avatar" src="${child.avatar || 'https://placehold.co/44'}" alt="${child.firstName || ''}">
        <div class="ow-child-card__info">
          <div class="ow-child-card__name">${child.firstName || ''} ${child.lastName || ''}</div>
          <div class="ow-child-card__detail">${child.school || 'No school'} &bull; ${child.gender === 'male' ? 'Boy' : 'Girl'} &bull; Grade ${child.grade || '?'}</div>
        </div>
        <button class="ow-child-card__remove" data-idx="${idx}" title="Remove child">&times;</button>
      `;

      // Toggle selection on card click
      card.addEventListener('click', (e) => {
        if (e.target.closest('.ow-child-card__remove')) return;
        if (selectedChildIds.has(childId)) {
          selectedChildIds.delete(childId);
        } else {
          selectedChildIds.add(childId);
        }
        renderChildrenList();
        updateStartShoppingBtn();
      });

      card.querySelector('.ow-child-card__remove').addEventListener('click', (e) => {
        e.stopPropagation();
        selectedChildIds.delete(childId);
        removeChild(idx);
      });

      els.childrenList.appendChild(card);
    });

    updateStartShoppingBtn();
  }

  function updateStartShoppingBtn() {
    const count = selectedChildIds.size;
    if (count === 0) {
      els.startShoppingBtn.textContent = 'Select at least one child';
      els.startShoppingBtn.disabled = true;
      els.startShoppingBtn.style.opacity = '0.5';
    } else {
      els.startShoppingBtn.textContent = count === children.length
        ? 'Start Shopping'
        : `Start Shopping for ${count} child${count > 1 ? 'ren' : ''}`;
      els.startShoppingBtn.disabled = false;
      els.startShoppingBtn.style.opacity = '1';
    }
  }

  function toggleAddChildForm(show) {
    els.addChildForm.classList.toggle('tw-hidden', !show);
    if (show) {
      els.childFirstName.value = '';
      els.childLastName.value = '';
      els.childGrade.value = '';
      els.childGender.value = '';
      populateSchoolSelect(els.childSchool);
      els.childFirstName.focus();
    }
  }

  async function loadSchoolCollections() {
    if (schoolCollections.length > 0) return;
    try {
      let page = 1;
      let all = [];
      while (true) {
        const res = await fetch(`/collections.json?page=${page}`);
        const data = await res.json();
        if (!data.collections || data.collections.length === 0) break;
        all.push(...data.collections);
        page++;
      }
      schoolCollections = all.filter(c =>
        c.title.toLowerCase().includes('school') || c.title.toLowerCase().includes('academy')
      );
    } catch (e) {
      console.error('Failed to load collections:', e);
    }
  }

  function populateSchoolSelect(selectEl, currentValue) {
    selectEl.innerHTML = '<option value="">Select School</option>';
    schoolCollections.forEach(c => {
      const opt = document.createElement('option');
      opt.value = c.title;
      opt.textContent = c.title;
      opt.dataset.handle = c.handle;
      if (currentValue && c.title === currentValue) opt.selected = true;
      selectEl.appendChild(opt);
    });
  }

  async function saveNewChild() {
    const firstName = els.childFirstName.value.trim();
    const school = els.childSchool.value;
    const grade = els.childGrade.value.trim();
    const gender = els.childGender.value;

    if (!firstName || !school || !grade || !gender) {
      alert('Please fill in all required fields.');
      return;
    }

    const selectedOpt = els.childSchool.selectedOptions[0];
    const collectionHandle = selectedOpt?.dataset?.handle || handleize(school);

    els.saveChildBtn.disabled = true;
    els.saveChildBtn.textContent = 'SAVING...';

    try {
      const customerId = getCustomerId();
      const res = await fetch(`${FIREBASE_BASE}/addChild`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customerId,
          child: {
            firstName,
            lastName: els.childLastName.value.trim(),
            school,
            collectionHandle,
            grade,
            gender
          }
        })
      });
      const data = await res.json();
      const newChild = data.child || data;
      children.push(newChild);
      toggleAddChildForm(false);
      renderChildrenList();
    } catch (e) {
      console.error('Failed to save child:', e);
      alert('Failed to save. Please try again.');
    } finally {
      els.saveChildBtn.disabled = false;
      els.saveChildBtn.textContent = 'Save Child';
    }
  }

  async function removeChild(idx) {
    const child = children[idx];
    if (!confirm(`Remove ${child.firstName}?`)) return;

    try {
      const customerId = getCustomerId();
      await fetch(`${FIREBASE_BASE}/removeChild`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customerId, childId: child.id })
      });
      children.splice(idx, 1);
      renderChildrenList();
    } catch (e) {
      console.error('Failed to remove child:', e);
    }
  }

  function startShoppingForChildren() {
    // Filter to only selected children
    shoppingChildren = children.filter(child => {
      const childId = child.id || '';
      return selectedChildIds.has(childId);
    });

    if (shoppingChildren.length === 0) {
      alert('Select at least one child first.');
      return;
    }
    currentChildIndex = 0;
    goToStep('profile');
  }

  // ── Profile Step (per child) ────────────────────────
  function renderProfileStep() {
    const child = shoppingChildren[currentChildIndex];
    renderChildBadge('owProfileChildBadge', child);

    populateSchoolSelect(els.profileSchool, child.school);
    els.profileGrade.value = child.grade || '';
    els.profileGender.value = child.gender || 'male';
  }

  async function saveProfileAndNext() {
    const child = shoppingChildren[currentChildIndex];
    const newSchool = els.profileSchool.value;
    const newGrade = els.profileGrade.value.trim();
    const newGender = els.profileGender.value;

    const selectedOpt = els.profileSchool.selectedOptions[0];
    const newHandle = selectedOpt?.dataset?.handle || handleize(newSchool);

    // Update locally
    child.school = newSchool;
    child.collectionHandle = newHandle;
    child.grade = newGrade;
    child.gender = newGender;

    // Persist to Firebase
    try {
      const customerId = getCustomerId();
      await fetch(`${FIREBASE_BASE}/updateChild`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customerId,
          childId: child.id,
          updatedData: { school: newSchool, collectionHandle: newHandle, grade: newGrade, gender: newGender }
        })
      });
    } catch (e) {
      console.warn('Failed to persist profile update:', e);
    }

    goToStep('sizes');
  }

  // ── Sizes Step (per child) ──────────────────────────
  function renderSizesStep() {
    const child = shoppingChildren[currentChildIndex];
    renderChildBadge('owSizesChildBadge', child);

    const hasSizes = child.topSize || child.bottomSize || child.outerwearSize;

    if (hasSizes) {
      els.existingSizes.classList.remove('tw-hidden');
      els.manualSizes.classList.add('tw-hidden');
      els.currentTopSize.textContent = child.topSize || '—';
      els.currentBottomSize.textContent = child.bottomSize || '—';
      els.currentOuterwearSize.textContent = child.outerwearSize || '—';
      // Pre-fill the manual inputs with current sizes so they're ready if the user switches to manual entry
      els.inputTopSize.value = child.topSize || '';
      els.inputBottomSize.value = child.bottomSize || '';
      els.inputOuterwearSize.value = child.outerwearSize || '';
      els.sizesSubtext.textContent = 'These are the saved sizes. You can bump them up, enter new ones, or use measurements.';
      els.toggleMeasurements.textContent = "I'd rather enter measurements";
    } else {
      els.existingSizes.classList.add('tw-hidden');
      els.manualSizes.classList.remove('tw-hidden');
      els.inputTopSize.value = '';
      els.inputBottomSize.value = '';
      els.inputOuterwearSize.value = '';
      els.sizesSubtext.textContent = 'Enter sizes directly, or use measurements for a recommendation.';
      els.toggleMeasurements.textContent = 'Use measurements instead';
    }

    // Reset measurement form
    els.measurementForm.classList.add('tw-hidden');
    els.sizeResults.classList.add('tw-hidden');
    els.measChest.value = '';
    els.measWaist.value = '';
    els.measHips.value = '';
    els.measInseam.value = '';
    els.measWaistKnee.value = '';
    els.measShoulder.value = '';
  }

  function bumpAllSizes() {
    const child = shoppingChildren[currentChildIndex];
    const isBoy = child.gender === 'male';

    child.topSize = bumpSize(child.topSize, isBoy);
    child.bottomSize = bumpSize(child.bottomSize, isBoy);
    child.outerwearSize = bumpSize(child.outerwearSize, isBoy);

    // Check if any size hit the ceiling
    const maxNumeric = isBoy ? MAX_BOYS_NUMERIC : MAX_GIRLS_NUMERIC;
    let hitCeiling = false;
    [child.topSize, child.bottomSize, child.outerwearSize].forEach(s => {
      const num = parseInt(s);
      if (!isNaN(num) && num > maxNumeric) hitCeiling = true;
    });

    if (hitCeiling) {
      // Show measurement form with waist bumped
      const currentWaist = parseFloat(els.measWaist.value) || 0;
      els.measWaist.value = (currentWaist + WAIST_BUMP_INCHES).toFixed(1);
      toggleMeasurementEntry();
      alert(`Some sizes exceeded the maximum (${isBoy ? 'Boys 20' : 'Girls 18'}). We've increased the waist measurement by ${WAIST_BUMP_INCHES}" — please review and get new recommendations.`);
    }

    // Re-render
    els.currentTopSize.textContent = child.topSize || '—';
    els.currentBottomSize.textContent = child.bottomSize || '—';
    els.currentOuterwearSize.textContent = child.outerwearSize || '—';
  }

  function bumpSize(size, isBoy) {
    if (!size) return size;

    // Handle sizes like "YM (10-12)" — extract letter prefix before parenthesized range
    const prefixedMatch = size.match(/^([A-Z]+)\s*\(.*\)$/i);
    if (prefixedMatch) {
      const letterPart = prefixedMatch[1].toUpperCase();
      const letterIdx = LETTER_SIZES.indexOf(letterPart);
      if (letterIdx >= 0 && letterIdx < LETTER_SIZES.length - 1) {
        return LETTER_SIZES[letterIdx + 1];
      }
    }

    // Try numeric (e.g. "10", "12 Slim")
    const numMatch = size.match(/^(\d+)(.*)/);
    if (numMatch) {
      const num = parseInt(numMatch[1]);
      const suffix = numMatch[2] || '';
      // Find next in NUMERIC_SIZES
      const idx = NUMERIC_SIZES.indexOf(String(num));
      if (idx >= 0 && idx < NUMERIC_SIZES.length - 1) {
        return NUMERIC_SIZES[idx + 1] + suffix;
      }
      // Just increment by 2 (uniform sizing convention)
      return String(num + 2) + suffix;
    }

    // Try letter sizes (e.g. "YM", "S", "XL")
    const upperSize = size.toUpperCase();
    const letterIdx = LETTER_SIZES.indexOf(upperSize);
    if (letterIdx >= 0 && letterIdx < LETTER_SIZES.length - 1) {
      return LETTER_SIZES[letterIdx + 1];
    }

    return size;
  }

  function toggleMeasurementEntry() {
    const isVisible = !els.measurementForm.classList.contains('tw-hidden');
    els.measurementForm.classList.toggle('tw-hidden', isVisible);
    els.toggleMeasurements.textContent = isVisible ? "I'd rather enter measurements" : 'Hide measurements';
  }

  async function resolveSizesFromMeasurements() {
    // Load sizing data if needed
    if (sizingData.length === 0) {
      els.resolveSizesBtn.disabled = true;
      els.resolveSizesBtn.textContent = 'Loading sizing data...';
      await loadSizingData();
      els.resolveSizesBtn.disabled = false;
      els.resolveSizesBtn.textContent = 'Get Size Recommendations';
    }

    const measurements = {
      chest: parseFloat(els.measChest.value) || null,
      waist: parseFloat(els.measWaist.value) || null,
      hips: parseFloat(els.measHips.value) || null,
      inseam: parseFloat(els.measInseam.value) || null,
      waist_to_knee: parseFloat(els.measWaistKnee.value) || null,
      shoulder_width: parseFloat(els.measShoulder.value) || null
    };

    if (!measurements.chest && !measurements.waist) {
      alert('Please enter at least chest or waist measurement.');
      return;
    }

    // Run the sizing algorithm for generic item types
    const topResult = findBestSize(measurements, 'top');
    const bottomResult = findBestSize(measurements, 'bottom');
    const outerwearResult = findBestSize(measurements, 'general');

    let html = '<h4 style="margin:0 0 12px;font-size:14px;color:#153640;font-weight:700;">Recommended Sizes</h4>';

    const rows = [
      { label: 'Top Size', sizes: topResult.sizes, note: topResult.note },
      { label: 'Bottom Size', sizes: bottomResult.sizes, note: bottomResult.note },
      { label: 'Outerwear Size', sizes: outerwearResult.sizes, note: outerwearResult.note }
    ];

    rows.forEach(row => {
      html += `<div class="ow-size-result-row">
        <span class="ow-size-result-item">${row.label}</span>
        <span>
          <span class="ow-size-result-rec">${row.sizes.length > 0 ? row.sizes.join(' / ') : '—'}</span>
          <span class="ow-size-result-note">${row.note}</span>
        </span>
      </div>`;
    });

    html += '<button id="owApplyRecommended" class="ow-btn ow-btn--primary ow-btn--small" style="margin-top:12px;">Apply These Sizes</button>';

    els.sizeResults.innerHTML = html;
    els.sizeResults.classList.remove('tw-hidden');

    // Bind the apply button
    document.getElementById('owApplyRecommended').addEventListener('click', () => {
      const child = shoppingChildren[currentChildIndex];
      if (topResult.sizes[0]) child.topSize = topResult.sizes[0];
      if (bottomResult.sizes[0]) child.bottomSize = bottomResult.sizes[0];
      if (outerwearResult.sizes[0]) child.outerwearSize = outerwearResult.sizes[0];

      // Update display
      els.existingSizes.classList.remove('tw-hidden');
      els.manualSizes.classList.add('tw-hidden');
      els.currentTopSize.textContent = child.topSize || '—';
      els.currentBottomSize.textContent = child.bottomSize || '—';
      els.currentOuterwearSize.textContent = child.outerwearSize || '—';

      // Also fill manual fields
      els.inputTopSize.value = child.topSize || '';
      els.inputBottomSize.value = child.bottomSize || '';
      els.inputOuterwearSize.value = child.outerwearSize || '';
    });
  }

  async function loadSizingData() {
    if (sizingData.length > 0 && requirementsData.length > 0) return;

    const urls = [];
    if (sizingConfig.sizingUrl) urls.push(fetch(sizingConfig.sizingUrl).then(r => r.text()));
    else urls.push(Promise.resolve(''));
    if (sizingConfig.requirementsUrl) urls.push(fetch(sizingConfig.requirementsUrl).then(r => r.text()));
    else urls.push(Promise.resolve(''));

    try {
      const [sizeText, reqText] = await Promise.all(urls);
      if (sizeText) sizingData = parseCSV(sizeText);
      if (reqText) requirementsData = parseCSV(reqText);
    } catch (e) {
      console.error('Failed to load sizing CSVs:', e);
    }
  }

  function parseCSV(text) {
    const lines = text.split('\n');
    const delimiter = lines[0].includes('\t') ? '\t' : ',';
    const headers = lines[0].split(delimiter).map(h => h.trim().replace(/"/g, ''));
    const data = [];

    for (let i = 1; i < lines.length; i++) {
      if (!lines[i].trim()) continue;
      let values;
      if (delimiter === '\t') {
        values = lines[i].split('\t').map(v => v.trim().replace(/"/g, ''));
      } else {
        values = parseCSVLine(lines[i]);
      }
      const row = {};
      headers.forEach((h, idx) => { row[h] = values[idx] || ''; });
      data.push(row);
    }
    return data;
  }

  function parseCSVLine(line) {
    const values = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') {
        if (inQuotes && i + 1 < line.length && line[i + 1] === '"') { current += '"'; i++; }
        else inQuotes = !inQuotes;
      } else if (ch === ',' && !inQuotes) {
        values.push(current.trim());
        current = '';
      } else {
        current += ch;
      }
    }
    values.push(current.trim());
    return values;
  }

  function findBestSize(measurements, type) {
    // type: 'top', 'bottom', 'skirt', 'general'
    if (sizingData.length === 0) return { sizes: [], note: 'No sizing data' };

    // Filter sizing rows — we don't know exact item, so use all available
    const rows = sizingData.filter(r => r.Size && (r.Chest_Min || r.Waist_Min));

    if (rows.length === 0) return { sizes: [], note: 'No sizing data' };

    const safeDiff = (mVal, min, max) => {
      if (mVal == null || !min || !max) return 0;
      const mid = (parseFloat(min) + parseFloat(max)) / 2;
      return Math.abs(mVal - mid);
    };

    const scored = rows.map(size => {
      let score;
      if (type === 'top') {
        score = safeDiff(measurements.chest, size.Chest_Min, size.Chest_Max);
      } else if (type === 'bottom' || type === 'skirt') {
        score = (safeDiff(measurements.waist, size.Waist_Min, size.Waist_Max) * 3) +
                (safeDiff(measurements.hips, size.Hip_Min, size.Hip_Max) * 2) +
                safeDiff(measurements.inseam, size.Inseam_Min, size.Inseam_Max);
      } else {
        score = safeDiff(measurements.chest, size.Chest_Min, size.Chest_Max) +
                safeDiff(measurements.waist, size.Waist_Min, size.Waist_Max) +
                safeDiff(measurements.hips, size.Hip_Min, size.Hip_Max);
      }
      return { size: size.Size, score };
    });

    scored.sort((a, b) => a.score - b.score);

    // Deduplicate sizes
    const seen = new Set();
    const unique = scored.filter(s => {
      if (seen.has(s.size)) return false;
      seen.add(s.size);
      return true;
    });

    const best = unique.slice(0, 3);
    const closeMatches = best.filter(s => s.score - best[0].score <= 1.0);

    return {
      sizes: closeMatches.map(s => s.size),
      note: closeMatches.length > 1 ? 'Multiple options' : 'Best match'
    };
  }

  async function saveSizesAndNext() {
    const child = shoppingChildren[currentChildIndex];

    // If manual sizes are visible, read from inputs
    if (!els.manualSizes.classList.contains('tw-hidden')) {
      const top = els.inputTopSize.value.trim();
      const bottom = els.inputBottomSize.value.trim();
      const outerwear = els.inputOuterwearSize.value.trim();
      if (top) child.topSize = top;
      if (bottom) child.bottomSize = bottom;
      if (outerwear) child.outerwearSize = outerwear;
    }

    // Persist sizes to Firebase
    try {
      const customerId = getCustomerId();
      await fetch(`${FIREBASE_BASE}/updateChild`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customerId,
          childId: child.id,
          updatedData: {
            topSize: child.topSize || '',
            bottomSize: child.bottomSize || '',
            outerwearSize: child.outerwearSize || ''
          }
        })
      });
    } catch (e) {
      console.warn('Failed to persist sizes:', e);
    }

    // Initialize selections for this child
    if (!selections[child.id]) selections[child.id] = [];

    goToStep('tops');
  }

  // ── Category Step ───────────────────────────────────
  function renderCategoryStep(category) {
    const child = shoppingChildren[currentChildIndex];
    renderChildBadge('owCatChildBadge', child);

    const label = capitalize(category);
    els.catHeading.textContent = label;

    // Show girls-cut preference only for tops on female children
    if (category === 'tops' && child.gender === 'female') {
      els.girlsCutPref.classList.remove('tw-hidden');
      els.catSubtext.textContent = 'Select polos and tops for ' + child.firstName + '.';
    } else {
      els.girlsCutPref.classList.add('tw-hidden');
      if (category === 'tops') {
        els.catSubtext.textContent = 'Select tops for ' + child.firstName + '. Showing unisex options.';
      } else {
        els.catSubtext.textContent = `Select ${label.toLowerCase()} for ${child.firstName}.`;
      }
    }

    // Update next button text
    const catIdx = CATEGORIES.indexOf(category);
    if (catIdx < CATEGORIES.length - 1) {
      els.catNextBtn.textContent = 'Next: ' + capitalize(CATEGORIES[catIdx + 1]);
    } else {
      els.catNextBtn.textContent = 'Review Selections';
    }

    renderCategoryProducts();
  }

  async function renderCategoryProducts() {
    const child = shoppingChildren[currentChildIndex];
    const category = currentStep;
    const schoolHandle = child.collectionHandle || handleize(child.school || '');

    els.productGrid.innerHTML = '<div class="ow-loading">Loading products...</div>';

    const products = await fetchProducts(schoolHandle, child.gender);

    // Filter by category tag
    let categoryProducts = products.filter(p => {
      const tags = getTags(p);
      return tags.includes(category);
    });

    // For tops: handle gender-based filtering
    if (category === 'tops') {
      if (child.gender === 'male') {
        // Boys: show unisex only
        categoryProducts = categoryProducts.filter(p => {
          const tags = getTags(p);
          return tags.some(t => ['unisex', 'boy', 'boys'].includes(t));
        });
      } else if (child.gender === 'female') {
        // Girls: check metafield for gender, apply cut preference
        if (cutPreference === 'girls') {
          categoryProducts = categoryProducts.filter(p => {
            const tags = getTags(p);
            return tags.some(t => ['girl', 'girls'].includes(t));
          });
        } else if (cutPreference === 'unisex') {
          categoryProducts = categoryProducts.filter(p => {
            const tags = getTags(p);
            return tags.includes('unisex');
          });
        }
        // 'both' shows everything already filtered by fetchProducts
      }
    }

    // Exclude spirit wear
    categoryProducts = categoryProducts.filter(p => {
      const tags = getTags(p);
      return !tags.includes('spirit wear');
    });

    if (categoryProducts.length === 0) {
      els.productGrid.innerHTML = '<div class="ow-loading">No products found in this category.</div>';
      return;
    }

    els.productGrid.innerHTML = '';

    // Check if girls-cut preference should be shown (only if both types exist)
    if (category === 'tops' && child.gender === 'female') {
      const hasGirls = categoryProducts.some(p => {
        const tags = getTags(p);
        return tags.some(t => ['girl', 'girls'].includes(t)) && !tags.includes('unisex');
      });
      const hasUnisex = categoryProducts.some(p => getTags(p).includes('unisex'));
      els.girlsCutPref.classList.toggle('tw-hidden', !(hasGirls && hasUnisex));
    }

    categoryProducts.forEach(product => {
      const row = renderProductRow(product, category, child);
      els.productGrid.appendChild(row);
    });
  }

  function renderProductRow(product, category, child) {
    const row = document.createElement('div');
    row.className = 'ow-product-row';

    const imgSrc = product.images?.[0]?.src ? product.images[0].src + '&width=150' : 'https://placehold.co/72';
    const price = product.variants?.[0]?.price ? formatMoney(product.variants[0].price) : '';

    // Determine preferred size for this category
    const preferredSize = getPreferredSize(child, category);

    // Build option selectors
    const options = product.options || [];
    const variants = product.variants || [];

    let optionsHtml = '';
    const sizeOptionIdx = options.findIndex(o => o.name.toLowerCase() === 'size');

    options.forEach((opt, optIdx) => {
      const key = `option${optIdx + 1}`;
      const values = [...new Set(variants.map(v => v[key]).filter(Boolean))];
      if (values.length <= 1) return;

      // Pre-select preferred size
      let selectedValue = values[0];
      if (opt.name.toLowerCase() === 'size' && preferredSize) {
        const match = values.find(v => matchesPreferredSize(v, preferredSize));
        if (match) selectedValue = match;
      }

      optionsHtml += `<select class="ow-variant-select" data-option-index="${optIdx}">
        ${values.map(v => `<option value="${v}" ${v === selectedValue ? 'selected' : ''}>${v}</option>`).join('')}
      </select>`;
    });

    // Check existing selection quantity for this product
    const childSelections = selections[child.id] || [];
    const existingSel = childSelections.find(s => s.product.id === product.id);
    const existingQty = existingSel ? existingSel.qty : 0;

    row.innerHTML = `
      <img class="ow-product-img" src="${imgSrc}" alt="${product.title}">
      <div class="ow-product-info">
        <p class="ow-product-title">${product.title}</p>
        <p class="ow-product-price">${price}</p>
        <div class="ow-product-options">${optionsHtml}</div>
      </div>
      <div class="ow-product-qty">
        <button class="ow-qty-minus" type="button">&minus;</button>
        <input type="text" class="ow-qty-val" value="${existingQty}" readonly>
        <button class="ow-qty-plus" type="button">+</button>
      </div>
    `;

    // Qty controls
    const qtyInput = row.querySelector('.ow-qty-val');
    const minusBtn = row.querySelector('.ow-qty-minus');
    const plusBtn = row.querySelector('.ow-qty-plus');

    const updateSelections = () => {
      const qty = parseInt(qtyInput.value) || 0;
      const selects = row.querySelectorAll('.ow-variant-select');
      const selectedOptions = {};
      selects.forEach(s => { selectedOptions[s.dataset.optionIndex] = s.value; });

      // Find matching variant
      const matched = variants.find(v => {
        return Array.from(selects).every(s => {
          const key = `option${parseInt(s.dataset.optionIndex) + 1}`;
          return v[key] === s.value;
        });
      }) || variants[0];

      // Update or remove from selections
      if (!selections[child.id]) selections[child.id] = [];
      const existing = selections[child.id].findIndex(s => s.product.id === product.id);

      if (qty > 0 && matched) {
        const entry = {
          product,
          variantId: matched.id,
          variant: matched,
          qty,
          category,
          selectedOptions
        };
        if (existing >= 0) {
          selections[child.id][existing] = entry;
        } else {
          selections[child.id].push(entry);
        }
      } else if (existing >= 0) {
        selections[child.id].splice(existing, 1);
      }
    };

    minusBtn.addEventListener('click', () => {
      let val = parseInt(qtyInput.value) || 0;
      if (val > 0) {
        qtyInput.value = val - 1;
        updateSelections();
      }
    });

    plusBtn.addEventListener('click', () => {
      let val = parseInt(qtyInput.value) || 0;
      qtyInput.value = val + 1;
      updateSelections();
    });

    // Update on variant change too
    row.querySelectorAll('.ow-variant-select').forEach(s => {
      s.addEventListener('change', () => {
        const qty = parseInt(qtyInput.value) || 0;
        if (qty > 0) updateSelections();
      });
    });

    return row;
  }

  function nextCategory() {
    const catIdx = CATEGORIES.indexOf(currentStep);
    if (catIdx < CATEGORIES.length - 1) {
      goToStep(CATEGORIES[catIdx + 1]);
    } else {
      goToStep('summary');
    }
  }

  // ── Summary Step ────────────────────────────────────
  function renderSummaryStep() {
    const child = shoppingChildren[currentChildIndex];
    renderChildBadge('owSumChildBadge', child);

    const items = selections[child.id] || [];
    els.nextChildPrompt.classList.add('tw-hidden');
    els.addAllToCartBtn.classList.remove('tw-hidden');

    if (items.length === 0) {
      els.summaryItems.classList.add('tw-hidden');
      els.summaryEmpty.classList.remove('tw-hidden');
      els.addAllToCartBtn.disabled = true;
      return;
    }

    els.summaryItems.classList.remove('tw-hidden');
    els.summaryEmpty.classList.add('tw-hidden');
    els.addAllToCartBtn.disabled = false;
    els.summaryItems.innerHTML = '';

    items.forEach((item, idx) => {
      const imgSrc = item.product.images?.[0]?.src ? item.product.images[0].src + '&width=120' : 'https://placehold.co/52';
      const variantTitle = item.variant?.title !== 'Default Title' ? item.variant?.title : '';
      const price = item.variant?.price ? formatMoney(item.variant.price) : '';

      const row = document.createElement('div');
      row.className = 'ow-summary-row';
      row.innerHTML = `
        <img class="ow-summary-img" src="${imgSrc}" alt="${item.product.title}">
        <div class="ow-summary-info">
          <p class="ow-summary-title">${item.product.title}</p>
          <p class="ow-summary-variant">${variantTitle} ${price ? '&bull; ' + price : ''}</p>
        </div>
        <div class="ow-summary-qty">x${item.qty}</div>
        <button class="ow-summary-remove" data-idx="${idx}" title="Remove">&times;</button>
      `;

      row.querySelector('.ow-summary-remove').addEventListener('click', () => {
        selections[child.id].splice(idx, 1);
        renderSummaryStep();
      });

      els.summaryItems.appendChild(row);
    });
  }

  async function addAllToCart() {
    const child = shoppingChildren[currentChildIndex];
    const items = selections[child.id] || [];
    if (items.length === 0) return;

    els.addAllToCartBtn.disabled = true;
    els.addAllToCartBtn.textContent = 'ADDING TO CART...';

    // Build payload — add items one at a time to attach properties
    let success = true;
    for (const item of items) {
      try {
        await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: item.variantId,
            quantity: item.qty,
            properties: {
              _child: child.id,
              Name: child.firstName?.trim() || '',
              _category: item.category,
              _handle: item.product.handle
            }
          })
        });
      } catch (e) {
        console.error('Failed to add item:', e);
        success = false;
      }
    }

    if (!success) {
      alert('Some items could not be added. Please try again.');
      els.addAllToCartBtn.disabled = false;
      els.addAllToCartBtn.textContent = 'Add All to Cart';
      return;
    }

    // Fire cart rebuild event
    document.dispatchEvent(new CustomEvent('cart:build'));

    // Clear selections for this child
    selections[child.id] = [];

    // Show next child prompt or finish
    els.addAllToCartBtn.classList.add('tw-hidden');
    els.summaryItems.innerHTML = '<div class="ow-loading" style="color:#2e7d32;">All items added to cart!</div>';
    els.nextChildPrompt.classList.remove('tw-hidden');

    if (currentChildIndex >= shoppingChildren.length - 1) {
      els.nextChildBtn.classList.add('tw-hidden');
    } else {
      els.nextChildBtn.classList.remove('tw-hidden');
      els.nextChildBtn.textContent = `Shop for ${shoppingChildren[currentChildIndex + 1].firstName}`;
    }
  }

  function goToNextChild() {
    currentChildIndex++;
    if (currentChildIndex < shoppingChildren.length) {
      stepHistory = ['children']; // reset history so back goes to children list
      goToStep('profile');
    }
  }

  function finishAndViewCart() {
    close();
    // Try to open custom cart, or navigate to /cart
    if (typeof window.openCustomCart === 'function') {
      window.openCustomCart();
    } else {
      window.location.href = '/cart';
    }
  }

  // ── Helpers ─────────────────────────────────────────
  function renderChildBadge(containerId, child) {
    const el = document.getElementById(containerId);
    if (!el) return;
    const avatar = child.avatar || 'https://placehold.co/24';
    const counter = `${currentChildIndex + 1} of ${shoppingChildren.length || children.length}`;
    el.innerHTML = `
      <img class="ow-child-badge__avatar" src="${avatar}" alt="">
      <span>${child.firstName || 'Child'}</span>
      <span class="ow-child-badge__counter">(${counter})</span>
    `;
  }

  function handleize(str) {
    return str.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  }

  function capitalize(str) {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  function formatMoney(cents) {
    // Shopify products.json returns price as a string like "29.99" or cents
    const amount = typeof cents === 'string' ? parseFloat(cents) : cents / 100;
    return '$' + amount.toFixed(2);
  }

  function getTags(product) {
    const raw = product.tags || [];
    return (typeof raw === 'string' ? raw.split(',') : raw).map(t => t.trim().toLowerCase());
  }

  function getPreferredSize(child, category) {
    switch (category) {
      case 'tops': return child.topSize || null;
      case 'bottoms': return child.bottomSize || null;
      case 'outerwear': return child.outerwearSize || null;
      default: return null;
    }
  }

  // Flexible size matching (ported from dashboard.js)
  function matchesPreferredSize(variantValue, preferredSize) {
    if (!preferredSize || !variantValue) return false;
    const nv = variantValue.trim().toLowerCase();
    const np = preferredSize.trim().toLowerCase();
    if (nv === np) return true;

    // Numeric match
    const pvNum = preferredSize.match(/(\d+)/);
    const vvNum = variantValue.match(/(\d+)/);
    if (pvNum && vvNum && pvNum[1] === vvNum[1]) return true;

    // Letter match with word boundary
    const letterMatch = preferredSize.match(/^([A-Z]+)/i);
    if (letterMatch) {
      const letters = letterMatch[1].toLowerCase();
      if (nv === letters) return true;
      const regex = new RegExp(`(^|[^a-z])${letters}($|[^a-z])`);
      if (regex.test(nv)) return true;
    }

    return false;
  }

  async function fetchProducts(schoolHandle, gender) {
    const cacheKey = `${schoolHandle}_${gender}`;
    if (productCache[cacheKey]) return productCache[cacheKey];

    try {
      let allProducts = [];
      let page = 1;
      while (true) {
        const res = await fetch(`/collections/${schoolHandle}/products.json?limit=250&page=${page}`);
        const data = await res.json();
        if (!data.products || data.products.length === 0) break;
        allProducts.push(...data.products);
        if (data.products.length < 250) break;
        page++;
      }

      // Gender filter
      const acceptedGenders = gender === 'male'
        ? ['boy', 'boys', 'unisex']
        : ['girl', 'girls', 'unisex'];

      const filtered = allProducts.filter(product => {
        const tags = getTags(product);
        return tags.some(t => acceptedGenders.includes(t));
      });

      productCache[cacheKey] = filtered;
      return filtered;
    } catch (e) {
      console.error('Failed to fetch products:', e);
      return [];
    }
  }

  // ── Start ───────────────────────────────────────────
  init();
})();
